import React from "react";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DSChip from "./DSChip";
import DSBox from "./DSBox";
import DSButton from "./DSButton";
import DSDialog from "./DSDialog";
import DSDialogContent from "./DSDialogContent";
import DSIconButton from "./DSIconButton";
import DSInput from "./DSInput";
import DSTypography from "./DSTypography";
import DSCircularProgress from "./DSCircularProgress";
import { useDSTagList } from "./hooks/useDSTagList";

export interface DSTagListProps {
  tags: string[];
  onSave: (tags: string[]) => Promise<void>;
  allTags: string[];
  isLoading?: boolean;
  showCopyButton?: boolean;
  placeholder?: string;
  title?: string;
}

const DSTagList: React.FC<DSTagListProps> = ({
  tags = [],
  onSave,
  allTags = [],
  isLoading = false,
  showCopyButton = false,
  placeholder = "New tag",
  title,
}) => {
  const {
    showModal,
    currentTags,
    searchText,
    copied,
    inputRef,
    setShowModal,
    setSearchText,
    addTag,
    removeTag,
    handleCopyTags,
    handleSave,
    handleCancel,
  } = useDSTagList({
    tags,
    allTags,
    onSave,
    isLoading,
  });

  const renderTags = () => {
    if (currentTags.length === 0) {
      return (
        <DSBox style={{ margin: "5px 0" }}>
          <DSTypography variant="body2" color="textSecondary">
            No tags
          </DSTypography>
        </DSBox>
      );
    }

    return (
      <DSBox style={{ display: "flex", flexWrap: "wrap", gap: "5px" }}>
        {currentTags.map((tag: string, index: number) => (
          <DSChip
            key={index}
            label={tag}
            onClick={() => setShowModal(true)}
            style={{ cursor: "pointer" }}
          />
        ))}
      </DSBox>
    );
  };

  return (
    <DSBox>
      <DSBox
        style={{
          display: "flex",
          flexDirection: "column",
          position: "relative",
        }}
        onClick={() => setShowModal(true)}
      >
        {title && (
          <DSBox style={{ marginBottom: "5px" }}>
            <DSTypography variant="subtitle1" fontWeight="bold">
              {title}
            </DSTypography>
          </DSBox>
        )}

        {renderTags()}

        {currentTags.length === 0 && (
          <DSIconButton
            size="small"
            sx={{
              alignSelf: "flex-start",
              mt: "5px",
              backgroundColor: "action.hover",
            }}
          >
            <AddIcon sx={{ fontSize: 14 }} />
          </DSIconButton>
        )}

        {showCopyButton && currentTags.length > 0 && navigator?.clipboard && (
          <DSIconButton
            title="Copy tags"
            size="small"
            sx={{
              position: "absolute",
              right: "1px",
              top: "1px",
              backgroundColor: "action.hover",
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleCopyTags();
            }}
          >
            {copied ? (
              <CheckIcon sx={{ fontSize: 14 }} />
            ) : (
              <ContentCopyIcon sx={{ fontSize: 14 }} />
            )}
          </DSIconButton>
        )}
      </DSBox>

      {showModal && (
        <DSDialog open onClose={handleCancel} fullWidth maxWidth="xs">
          <DSDialogContent>
            <DSBox
              tabIndex={1}
              onKeyDown={(ev) => {
                if (ev.key === "Enter" && searchText) {
                  ev.preventDefault();
                  addTag(searchText);
                } else if (ev.key === "Escape") {
                  handleCancel();
                }
              }}
              sx={{
                display: "flex",
                flexWrap: "wrap",
              }}
            >
              <DSBox
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                }}
              >
                <DSBox
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                  }}
                >
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      if (searchText) {
                        addTag(searchText);
                      }
                    }}
                    style={{ flex: 1 }}
                  >
                    <DSInput
                      inputRef={inputRef}
                      autoFocus
                      fullWidth
                      disableUnderline
                      placeholder={placeholder}
                      value={searchText}
                      sx={{
                        m: "5px",
                        p: 0,
                        fontSize: "14px",
                        color: "text.secondary",
                      }}
                      onChange={(e) => setSearchText(e.target.value)}
                    />
                  </form>

                  {searchText ? (
                    <DSIconButton
                      title="add"
                      size="small"
                      sx={{
                        alignSelf: "center",
                        backgroundColor: "action.hover",
                      }}
                      onClick={() => addTag(searchText)}
                    >
                      <AddIcon sx={{ fontSize: 14 }} />
                    </DSIconButton>
                  ) : null}
                </DSBox>

                <DSBox
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                  }}
                >
                  <DSTypography variant="caption" padding={0.625} margin={0}>
                    Current tags:
                  </DSTypography>
                  <DSBox
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "5px",
                      p: "5px",
                    }}
                  >
                    {currentTags.length === 0 ? (
                      <DSTypography
                        variant="caption"
                        color="textSecondary"
                        fontStyle="italic"
                      >
                        No tags added
                      </DSTypography>
                    ) : (
                      currentTags.map((tag: string, index: number) => (
                        <DSChip
                          key={index}
                          label={tag}
                          onDelete={() => removeTag(tag)}
                          style={{ margin: "2px" }}
                        />
                      ))
                    )}
                  </DSBox>
                </DSBox>

                <DSBox
                  sx={{
                    height: "1px",
                    width: "100%",
                    backgroundColor: "divider",
                    my: "10px",
                  }}
                />

                <DSBox
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                  }}
                >
                  <DSTypography variant="caption" padding={0.625} margin={0}>
                    All tags:
                  </DSTypography>
                  <DSBox
                    sx={{
                      maxHeight: 350,
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "5px",
                      p: "5px",
                      overflowY: "auto",
                    }}
                  >
                    {isLoading ? (
                      <DSCircularProgress size={20} />
                    ) : allTags.length === 0 ? (
                      <DSTypography
                        variant="caption"
                        color="textSecondary"
                        fontStyle="italic"
                      >
                        No matching tags
                      </DSTypography>
                    ) : (
                      allTags.map((tag: string, index: number) => (
                        <DSChip
                          key={index}
                          label={tag}
                          onClick={() => addTag(tag)}
                          style={{ margin: "2px", cursor: "pointer" }}
                        />
                      ))
                    )}
                  </DSBox>
                </DSBox>

                <DSBox
                  sx={{
                    height: "1px",
                    width: "100%",
                    backgroundColor: "divider",
                    my: "10px",
                  }}
                />

                <DSBox
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    width: "100%",
                    p: "5px",
                  }}
                >
                  <DSButton
                    variant="outlined"
                    color="secondary"
                    style={{ marginRight: "8px" }}
                    onClick={handleCancel}
                  >
                    Cancel
                  </DSButton>
                  <DSButton
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                  >
                    Save
                  </DSButton>
                </DSBox>
              </DSBox>
            </DSBox>
          </DSDialogContent>
        </DSDialog>
      )}
    </DSBox>
  );
};

export default DSTagList;
